import type { AskImage } from '@/lib/api/types';

/**
 * Formatos que a interpretação de imagens aceita. É a mesma lista do backend:
 * recusar aqui evita uma viagem inteira (upload + fila + visão) para receber um
 * 422 três minutos depois.
 */
export const ACCEPTED_IMAGE_TYPES = ['image/png', 'image/jpeg', 'image/webp'];

/** Teto de 5 MB por anexo — em base64 o corpo já cresce ~33% acima disso. */
export const MAX_IMAGE_BYTES = 5 * 1024 * 1024;

/** Uma imagem anexada a uma pergunta: o que viaja no fio, mais o nome do arquivo. */
export interface ChatImage extends AskImage {
  name: string;
}

/**
 * Ou o anexo pronto, ou o motivo da recusa em pt-BR — nomeando o valor recusado,
 * como os 422 da API. Nunca uma exceção: anexar o arquivo errado é uso normal.
 */
export type ImageAttachmentResult =
  | { ok: true; image: ChatImage }
  | { ok: false; reason: string };

/** A imagem como `src` de um `<img>` (a miniatura do chip e a do turno). */
export function imageDataUrl(image: AskImage): string {
  return `data:${image.mimetype};base64,${image.dataB64}`;
}

function megabytes(bytes: number): string {
  return (bytes / (1024 * 1024)).toFixed(1).replace('.', ',');
}

function readBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(reader.error ?? new Error('leitura falhou'));
    reader.onload = () => {
      // readAsDataURL entrega "data:<mime>;base64,<dados>"; o fio quer só os dados.
      const result = String(reader.result);
      resolve(result.slice(result.indexOf(',') + 1));
    };
    reader.readAsDataURL(file);
  });
}

/** Valida o arquivo escolhido (ou colado) e o converte para base64. */
export async function readImageAttachment(file: File): Promise<ImageAttachmentResult> {
  if (!ACCEPTED_IMAGE_TYPES.includes(file.type)) {
    return {
      ok: false,
      reason: `Formato não suportado: ${file.type || 'desconhecido'}. Anexe PNG, JPEG ou WebP.`,
    };
  }
  if (file.size > MAX_IMAGE_BYTES) {
    return {
      ok: false,
      reason: `Imagem muito grande: ${megabytes(file.size)} MB. O limite é 5 MB.`,
    };
  }
  try {
    const dataB64 = await readBase64(file);
    return { ok: true, image: { dataB64, mimetype: file.type, name: file.name || 'imagem' } };
  } catch {
    return { ok: false, reason: 'Não foi possível ler o arquivo escolhido. Tente outro.' };
  }
}
